// Modal for creating and editing Google Sheet data-fetch campaigns (WP-13).
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  saveSheetFetch,
  type SheetFetchDoc,
} from '../../lib/httpApi';
import {
  googleAccountsList,
  type GoogleAccountDoc,
} from '../../lib/google';

interface SheetFetchModalProps {
  open: boolean;
  onClose: () => void;
  fetchDoc: SheetFetchDoc | null;
  onSaved: () => void;
}

export function SheetFetchModal({ open, onClose, fetchDoc, onSaved }: SheetFetchModalProps) {
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [tab, setTab] = useState('Sheet1');
  const [keyColumn, setKeyColumn] = useState('');

  const [accounts, setAccounts] = useState<GoogleAccountDoc[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setLoadingAccounts(true);
    googleAccountsList()
      .then((res) => {
        setAccounts(res.accounts || []);
        if (!fetchDoc && res.accounts && res.accounts.length > 0) {
          setAccountId(res.accounts[0].id);
        }
      })
      .catch(() => {
        setAccounts([]);
      })
      .finally(() => {
        setLoadingAccounts(false);
      });

    if (fetchDoc) {
      setName(fetchDoc.name);
      setAccountId(fetchDoc.accountId);
      setSheetId(fetchDoc.sheetId);
      setTab(fetchDoc.tab || 'Sheet1');
      setKeyColumn(fetchDoc.keyColumn);
    } else {
      setName('');
      setSheetId('');
      setTab('Sheet1');
      setKeyColumn('');
    }
    setError(null);
  }, [open, fetchDoc]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !accountId.trim() || !sheetId.trim() || !keyColumn.trim()) {
      setError(t('httpApi.sheet.requiredFieldsError'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await saveSheetFetch({
        id: fetchDoc?.id,
        name: name.trim(),
        accountId: accountId.trim(),
        sheetId: sheetId.trim(),
        tab: tab.trim() || 'Sheet1',
        keyColumn: keyColumn.trim(),
      });

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || t('httpApi.sheet.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={fetchDoc ? t('httpApi.sheet.editTitle') : t('httpApi.sheet.createTitle')}
    >
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pe-1">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {/* Campaign Name */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
            {t('httpApi.sheet.name')} *
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('httpApi.sheet.namePlaceholder')}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
          />
        </div>

        {/* Google Account Selector */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
            {t('httpApi.sheet.googleAccount')} *
          </label>
          {loadingAccounts ? (
            <p className="text-xs text-slate-400">{t('common.loading')}</p>
          ) : accounts.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200 space-y-1">
              <p>{t('httpApi.sheet.noGoogleAccount')}</p>
              <Link
                to="/google-integration"
                className="font-bold underline hover:text-amber-900 dark:hover:text-white"
              >
                {t('httpApi.sheet.connectGoogleLink')} →
              </Link>
            </div>
          ) : (
            <select
              required
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            >
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.email}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Sheet ID or URL */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
            {t('httpApi.sheet.sheetIdOrUrl')} *
          </label>
          <input
            type="text"
            required
            value={sheetId}
            onChange={(e) => setSheetId(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs.../edit"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono text-xs"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {t('httpApi.sheet.sheetIdHint')}
          </p>
        </div>

        {/* Tab & Key Column */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('httpApi.sheet.tabName')} *
            </label>
            <input
              type="text"
              required
              value={tab}
              onChange={(e) => setTab(e.target.value)}
              placeholder="Sheet1"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
              {t('httpApi.sheet.keyColumn')} *
            </label>
            <input
              type="text"
              required
              value={keyColumn}
              onChange={(e) => setKeyColumn(e.target.value)}
              placeholder="Phone"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900 font-mono"
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-400">
          {t('httpApi.sheet.keyColumnHint')}
        </p>

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
